import { notify } from '@/utils/helpers/Notify'
import { validate } from '@/utils/helpers/Validate'

/**
 * Handles transaction selection
 * @param self        Component instance
 * @param selection   Selected models
 */
export function handleSelectionChange (self, selection) {
  let notThisPage = self.selection.filter(v => v.page !== self.currentPage)
  self.selection = [...notThisPage, ...selection]
}

/**
 * Preserves the selected transactions and shows them as selected whenever the table is re-rendered
 * due to a page or tab change
 * @param self      The component instance
 * @param tableRef  The current component table's 'ref' value
 */
export function restoreSelection (self, tableRef) {
  let table = self.$refs[tableRef]
  if (table) {
    table.$nextTick(() => {
      const thisPageSelectedIds = self.selection
        .filter(v => v.page === self.currentPage)
        .map(v => table.data.find(x => x.id === v.id))
      thisPageSelectedIds.forEach(v => table.toggleRowSelection(v, true))
    })
  }
}

/**
 * Handles what happens when the user clicks on one of the "With selected..." dropdown options
 * @param self      The component instance
 * @param payload
 */
export function handleBatchUpdateSelection (self, payload) {
  switch (self.batchUpdateSelection) {
    case 0:
      batchUpdate(self, 'delete', payload)
      break
  }
  self.batchUpdateSelection = null
}

/**
 * Resets editMode data, resets form to initial state, and displays the
 * modal to create a new model
 * @param self        Component instance
 */
export function createNewModel (self) {
  const resetNestedValueFields = () => {
    for (let field in self.form) {
      if (typeof self.form[field] === 'object' &&
        self.form[field].hasOwnProperty('label') &&
        self.form[field].hasOwnProperty('value')) {
        self.form[field] = Object.assign({}, { label: '', value: '' })
      }
    }
  }
  self.editMode.active = false
  self.editMode.modelId = null
  self.form = Object.assign({}, self.formInitState)
  resetNestedValueFields()
  self.initialForm = JSON.stringify(self.form)
  if (self.handleDialogVisible && typeof self.handleDialogVisible === 'function') {
    self.handleDialogVisible(true)
  } else {
    self.dialogFormVisible = true
  }
}

/**
 * Handles paginating between Insurances pages
 * @param opts
 */
export function changePage (opts) {
  let { self, page, payload, query, getDeps } = opts
  const { clearMutation } = payload
  const filters = self.$store.getters.getFilters
  if (filters && Object.keys(filters).length) {
    query = filters
  }
  let newPayload = query ? Object.assign({}, payload, { page, query }) : Object.assign({}, payload, { page })
  return self.$store.dispatch('clearLocalState', { clearMutation, clearCache: false })
    .then(() => self.$store.dispatch('fetchByPage', newPayload))
    .then((entities) => {
      if (!getDeps) {
        return []
      }
      return self.$store.dispatch('findDependencies', entities)
    })
    .then(promises => Promise.all(promises))
    .then(() => {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          self.changingPage = false
          resolve()
        })
      })
    })
    .then(() => {
      if (self.restoreSelection && typeof self.restoreSelection === 'function') {
        self.restoreSelection()
      }
    })
    .catch(err => {
      self.changingPage = false
      notify(self, 'error', 'Unable to fetch next page data')
      console.log(err)
    })
}

/*
* Validates and submits form data for basic system models (not data-entry models like JEs, APs, PCs, and POs)
* @param VueComponent self
* @param String formattedEntityName
*/
export function submitFormBasic (self, payload) {
  const handleNestedValueFields = (form) => {
    for (let field in form) {
      if (self.form[field] && typeof self.form[field] === 'object' && self.form[field].hasOwnProperty('value')) {
        form[field] = self.form[field].value
      }
    }
    return form
  }
  let form = Object.assign({}, self.form)
  let validationErrors = validate(form, self.fields)
  if (!validationErrors.messages) {
    form = handleNestedValueFields(form)
    form = self.formatPostOrPatchRequest(form)
    let newPayload = Object.assign({}, payload, { id: self.editMode.modelId, data: form })
    self.$store.dispatch(self.editMode.active ? 'update' : 'save', newPayload)
      .then(r => {
        if (r) {
          self.$store.dispatch('isEditingForm', false)
          if (self.handleDialogVisible && typeof self.handleDialogVisible === 'function') {
            self.handleDialogVisible(false)
          } else {
            self.dialogFormVisible = false
          }
          self.highlightNewModel(r.id)
          notify(self, 'success', `Saved`, 0)
        } else {
          const err = self.saveFailure
          notify(self, 'error', err || 'Error - please try again', 0)
        }
        if (self.restoreSelection && typeof self.restoreSelection === 'function') {
          self.restoreSelection()
        }
      })
      .catch(e => {
        console.log(e)
        const msg = self.editMode.active ? self.updateFailure.err : self.saveFailure
        notify(self, 'error', msg || `Error saving - please try again`, 0)
      })
  } else {
    let offset = 0                                                                                  // Set offset so that notifs don't overlap each other
    validationErrors.messages.map(v => {                                                            // Render validation error notifs
      notify(self, 'warning', v, offset)
      offset += 100
    })
  }
}

/**
 * Performs updates on multiple models at once
 * @param self
 * @param operation
 * @param payload
 * @param data
 */
export function batchUpdate (self, operation, payload, data = null) {
  const { selection } = self
  let promises = []
  for (let i = 0; i < selection.length; i++) {
    const { id } = selection[i]
    let newPayload
    if (operation === 'update') {
      newPayload = Object.assign({}, payload, { id, data })
      promises.push(self.$store.dispatch('update', newPayload))
    } else if (operation === 'delete') {
      newPayload = Object.assign({}, payload, { id })
      promises.push(self.$store.dispatch('delete', newPayload))
    }
  }
  Promise.all(promises)
    .then(() => {
      if (self.selection && self.selection.length) {
        self.selection = []
      }
    })
    .catch(e => console.log(e))
}

/**
 * Converts text to TitleCase
 * @param self
 * @param payload
 */
export function titleCase (self, payload) {
  const { selection } = self
  let promises = []
  const convertToTitleCase = (name) => {
    let newName = name.toLowerCase()
    let strSplit = newName.split(' ')
    for (let i = 0; i < strSplit.length; i++) {
      strSplit[i] = strSplit[i].charAt(0).toUpperCase() + strSplit[i].slice(1)
    }
    return strSplit.join(' ')
  }
  for (let i = 0; i < selection.length; i++) {
    const { id } = selection[i]
    let newName = convertToTitleCase(selection[i].name)
    let newPayload = Object.assign({}, payload, { id, data: { name: newName } })
    promises.push(self.$store.dispatch('update', newPayload))
  }
  Promise.all(promises)
    .then(() => {
      if (self.selection && self.selection.length) {
        self.selection = []
      }
    })
    .catch(e => console.log(e))
}

/**
 * Converts text to UPPER CASE
 * @param self
 * @param payload
 */
export function upperCase (self, payload) {
  const { selection } = self
  let promises = []
  for (let i = 0; i < selection.length; i++) {
    const { id } = selection[i]
    let newName = selection[i].name.toUpperCase()
    let newPayload = Object.assign({}, payload, { id, data: { name: newName } })
    promises.push(self.$store.dispatch('update', newPayload))
  }
  Promise.all(promises)
    .then(() => {
      if (self.selection && self.selection.length) {
        self.selection = []
      }
    })
    .catch(e => console.log(e))
}



// WEBPACK FOOTER //
// ./src/components/RouteComponents/commonFunctions.js